import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { PIE_CHART_COLOR } from '../enums';
import { getStocksTechnicalChart } from '@/src/services/stockDetails';

const RADIAN = Math.PI / 180;

interface MeterChartData {
  name: string;
  value: number;
  color: string;
}

interface TechnicalChartData {
  co_code: number;
  co_name: string;
  currprice: number;
  PivotPoint: number;
  S1: number;
  S2: number;
  S3: number;
  R1: number;
  R2: number;
  R3: number;
  CurrTime: string;
}

const MainContainer = styled.div`
  width: 210px;
  height: 110px;
`;

export const TechnicalPieChart: React.FC = () => {
  const [pieChartData, setPieChartData] = useState<TechnicalChartData | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const exchange = document
    .getElementById('csr-stock-technical-graph-section')
    ?.getAttribute('data-exchange');
  const cocode = document
    .getElementById('csr-stock-technical-graph-section')
    ?.getAttribute('data-cocode');

  useEffect(() => {
    if (cocode && exchange) {
      setError(null);
      getStocksTechnicalChart(cocode, exchange.toString())
        .then((response) => {
          const data = response.data[0];
          setPieChartData(data);
        })
        .catch(() => {
          setError('Failed to load chart data');
        });
    }
  }, [exchange, cocode]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const data: MeterChartData[] = [
    { name: 'S1', value: 20, color: PIE_CHART_COLOR.GREEN },
    { name: 'S2', value: 20, color: PIE_CHART_COLOR.GREEN },
    { name: 'S3', value: 20, color: PIE_CHART_COLOR.GREEN },
    { name: 'R1', value: 20, color: PIE_CHART_COLOR.RED },
    { name: 'R2', value: 20, color: PIE_CHART_COLOR.RED },
    { name: 'R3', value: 20, color: PIE_CHART_COLOR.RED },
  ];

  const value = pieChartData?.currprice ?? 0;
  const minValue = Math.min(
    pieChartData?.S1 ?? 0,
    pieChartData?.S2 ?? 0,
    pieChartData?.S3 ?? 0,
    pieChartData?.R1 ?? 0,
    pieChartData?.R2 ?? 0,
    pieChartData?.R3 ?? 0,
  );
  const maxValue = Math.max(
    pieChartData?.S1 ?? 0,
    pieChartData?.S2 ?? 0,
    pieChartData?.S3 ?? 0,
    pieChartData?.R1 ?? 0,
    pieChartData?.R2 ?? 0,
    pieChartData?.R3 ?? 0,
  );

  let checkValue;
  if (value < minValue) {
    checkValue = minValue;
  } else if (value > maxValue) {
    checkValue = maxValue;
  } else {
    checkValue = value;
  }
  const totalRange = maxValue - minValue;
  const valuePositionPercentage = 1 - (checkValue - minValue) / totalRange;
  const totalDegrees = 180;
  const angle = valuePositionPercentage * totalDegrees;
  const cx = 100;
  const cy = 100;
  const iR = 50;
  const oR = 100;

  const needle = (
    cx: number,
    cy: number,
    iR: number,
    oR: number,
    color: string,
  ) => {
    const length = (iR + 2 * oR) / 4;
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.cos(-RADIAN * angle);
    const r = 4;
    const x0 = cx;
    const y0 = cy;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;
    const needleWidth = 2;
    const needlePath = `M${x0 + 8 * cos} ${y0 + 8 * sin} L${xp} ${yp}`;

    return [
      <circle
        key="needle-circle"
        cx={x0}
        cy={y0}
        r={r}
        fill={'#fff'}
        strokeWidth={2}
        stroke={color}
      />,
      <path
        key="needle-path"
        d={needlePath}
        stroke={color}
        strokeWidth={needleWidth}
        fill="none"
      />,
    ];
  };

  return (
    <MainContainer>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            dataKey="value"
            startAngle={180}
            endAngle={0}
            data={data}
            cx={cx}
            cy={cy}
            innerRadius={94}
            outerRadius={100}
            fill="#fff"
            stroke="none"
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                stroke="#fff"
                strokeWidth={3}
              />
            ))}
          </Pie>
          {needle(cx, cy, iR, oR, '#000')}
        </PieChart>
      </ResponsiveContainer>
    </MainContainer>
  );
};
